import { useMutation, useQueryClient } from 'react-query';
import type { QueryClient } from 'react-query';

import axiosClient from '../../api/axios-client';
import type { ApiError } from '../../api/useApi';
import type { CleaningType, DndReason, DndSource } from '../../pages/housekeeping/types/housekeeping';

/**
 * Les gestes de l'agent sur « Mes chambres » — lot 3 Odomo.
 *
 * Mêmes appels que le web (`useStartCleaning`, `useCompleteCleaning`,
 * `useCompleteCleaningWithChecklist`, `useCompleteServiceRequest`, `useDeclareDnd`,
 * `useResolveDnd`), recopiés pour la frontière du socle.
 *
 * Aucun toast : l'écran dit le résultat EN LIGNE (`role=status` / `role=alert`).
 * Les hooks ne portent donc ni message ni notification, seulement l'invalidation.
 */

/** Tout ce qui commence par `/housekeeping` : liste, réglages, checklists, fiche. */
export function invaliderMenage(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({
        predicate: (query) => {
            const cle = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
            return typeof cle === 'string' && cle.startsWith('/housekeeping');
        },
    });
}

/** Une mutation ménage : l'appel, puis l'invalidation sur succès seulement. */
function useGesteMenage<TVariables>(appel: (variables: TVariables) => Promise<unknown>) {
    const queryClient = useQueryClient();
    return useMutation<unknown, ApiError, TVariables>(appel, {
        onSuccess: () => invaliderMenage(queryClient),
    });
}

export interface DemarrerMenage {
    serviceId: number;
    cleaning_type: CleaningType;
    override_comment?: string;
}

export function useDemarrerMenage() {
    return useGesteMenage(({ serviceId, ...corps }: DemarrerMenage) =>
        axiosClient.post(`/housekeeping/rooms/${serviceId}/start-cleaning`, corps),
    );
}

export function useTerminerMenage() {
    return useGesteMenage(({ serviceId }: { serviceId: number }) =>
        axiosClient.post(`/housekeeping/rooms/${serviceId}/complete-cleaning`),
    );
}

export interface TerminerAvecChecklist {
    serviceId: number;
    checklists: { template_id: number; completed_items: number[] }[];
    notes?: string;
}

export function useTerminerAvecChecklist() {
    return useGesteMenage(({ serviceId, ...corps }: TerminerAvecChecklist) =>
        axiosClient.post(`/housekeeping/rooms/${serviceId}/complete-with-checklist`, corps),
    );
}

/** Comme le tableau : cocher vaut validation d'une checklist produit éventuelle. */
export function useCocherPreparation() {
    return useGesteMenage(({ id }: { id: number }) =>
        axiosClient.post(`/housekeeping/service-requests/${id}/complete`, { checklist_completed: true }),
    );
}

export interface DeclarerNePasDeranger {
    serviceId: number;
    source: DndSource;
    reason: DndReason;
    note?: string;
}

export function useDeclarerNePasDeranger() {
    return useGesteMenage(({ serviceId, ...corps }: DeclarerNePasDeranger) =>
        axiosClient.post(`/housekeeping/rooms/${serviceId}/dnd`, corps),
    );
}

export function useLeverNePasDeranger() {
    return useGesteMenage(({ serviceId }: { serviceId: number }) =>
        axiosClient.delete(`/housekeeping/rooms/${serviceId}/dnd`),
    );
}
